/**
 * AuditLog — /w/:slug/app/audit
 *
 * First cut of the UC-7 Audit Trail surface per docs/ux-design-plan.md
 * §10 Cluster 7 close-out + docs/uc-2-provenance-api-spec.md §7.1.
 * Backend routes shipped in Phase D; this page consumes them.
 *
 * Scope of this first slice (PR-Cluster-7-a):
 *   - Read-only event window from GET /api/w/:slug/audit
 *   - Basic filter row: event-type multiselect + target-kind picker
 *   - Chain-verify status pill triggered by a button (calls
 *     GET /api/w/:slug/audit/chain-verify and shows verified/gaps count)
 *   - Row → event-detail drill via a right-side <details> disclosure
 *     (event-detail modal deferred to PR-Cluster-7-b)
 *
 * Deferred:
 *   - Chained prev/next hash rail per UC-7 R2 BC8
 *   - Snapshot-mismatch warning banner (needs snapshot storage)
 *   - Audit-package export generator UI + all export-tier flows
 *   - Assessor scope-pill variant (Cluster 6 dependency)
 *   - Mobile shell (P-UC7-1 punch list item)
 *   - Sensitive-field redaction toggles on the export tier
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Badge, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { listAuditEvents, verifyAuditChain, type AuditEventRow, type ChainVerifyReport } from '../lib/api';
import { fmtDate, eventTypeLabel } from '../lib/format';
import type { AuditEventType } from '@aigovops/core';

const EVENT_TYPE_GROUPS: Array<{ label: string; types: string[] }> = [
  {
    label: 'Register',
    types: [
      'item.status_changed',
      'item.attested',
      'item.reopened',
      'item.assignee_changed',
      'item.reviewer_changed',
    ],
  },
  {
    label: 'Evidence',
    types: ['evidence.added', 'evidence.removed', 'evidence.superseded'],
  },
  {
    label: 'Assessor',
    types: [
      'assessor.engagement_started',
      'assessor.engagement_ended',
      'assessor.question_posted',
      'assessor.question_addressed',
      'assessor.evidence_requested',
      'assessor.evidence_provided',
      'assessor.item_annotated',
      'assessor.annotation_disputed',
      'assessor.chain_verified',
    ],
  },
  {
    label: 'Package',
    types: ['package.signed'],
  },
  {
    label: 'Scoring',
    types: ['readiness.score_changed', 'methodology.bumped', 'deadline.crossed'],
  },
  {
    label: 'Retention',
    types: ['audit.hold_placed', 'audit.hold_released', 'audit.row_archived'],
  },
  // PR-P7-4 BH16 sweep: these three groups existed as emitted event
  // types with zero filter-chip coverage — same silent-firehose bug
  // the alert.* group comment above documents for PR-P3-3.
  {
    label: 'Comments',
    types: ['comment.added'],
  },
  {
    label: 'Webhooks',
    types: ['webhook.delivered', 'webhook.test', 'webhook.replayed'],
  },
  {
    label: 'Questionnaires',
    types: ['question.assignee_changed'],
  },
  {
    label: 'Workspace',
    types: ['auth.sso_enabled', 'auth.sso_disabled'],
  },
  // PR-P3-3. `alert.converted`/`alert.closed` shipped in PR-P3-1 but were
  // never added here, so no alert event has ever been filterable — and
  // `KNOWN_EVENT_TYPES` below DROPS unrecognised values, so a deep link
  // to ?type=alert.forwarded silently resolved to an unfiltered firehose.
  // The forward flow's chain-of-custody copy links here by event type,
  // so the group has to exist for that link to mean anything.
  {
    label: 'Alerts',
    types: [
      'alert.converted',
      'alert.closed',
      'alert.forwarded',
      'alert.forward_opened',
      'alert.forward_revoked',
    ],
  },
];

// PR-Cluster-7-h: URL-persisted event-type filter. Uses repeated
// `?type=<event_type>` params (a first-class multi-select URL shape)
// so refresh keeps the selection, back/forward navigates through
// filter history, and a link like /audit?type=item.status_changed&
// type=evidence.added deep-links straight to a scoped window.
// Unknown values are dropped rather than throwing; empty selection
// clears every `type` param so the canonical URL is /audit. Same
// "predicate + typed value + replace: true + default-clears-param"
// recipe as the Roadmap deadline filter and Register.tsx filters.
const KNOWN_EVENT_TYPES = new Set<string>(
  EVENT_TYPE_GROUPS.flatMap((g) => g.types),
);

export function AuditLog() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTypes = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const v of searchParams.getAll('type')) {
      if (KNOWN_EVENT_TYPES.has(v)) set.add(v);
    }
    return set;
  }, [searchParams]);
  const [chainReport, setChainReport] = useState<ChainVerifyReport | null>(null);
  const [chainBusy, setChainBusy] = useState(false);
  const [chainError, setChainError] = useState<string | null>(null);

  const eventTypeArg = useMemo(
    () => (selectedTypes.size > 0 ? Array.from(selectedTypes) : undefined),
    [selectedTypes],
  );

  const events = useWorkspaceQuery(
    (slug) => queryKeys.auditEvents(slug, eventTypeArg),
    (slug) => listAuditEvents(slug, { limit: 100, eventType: eventTypeArg }),
  );

  const writeTypes = useCallback(
    (next: Set<string>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('type');
          for (const t of next) params.append('type', t);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleType = useCallback(
    (t: string) => {
      const next = new Set(selectedTypes);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      writeTypes(next);
    },
    [selectedTypes, writeTypes],
  );

  const clearTypes = useCallback(() => {
    writeTypes(new Set());
  }, [writeTypes]);

  const runChainVerify = useCallback(async () => {
    if (!slug) return;
    setChainBusy(true);
    setChainError(null);
    try {
      const report = await verifyAuditChain(slug, { limit: 500 });
      setChainReport(report);
    } catch (err) {
      setChainError(err instanceof Error ? err.message : 'Chain verify failed.');
      setChainReport(null);
    } finally {
      setChainBusy(false);
    }
  }, [slug]);

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <PageHeader
        title="Audit log"
        subtitle="Every state change captured, chained, signed."
        right={
          <button
            type="button"
            onClick={() => void runChainVerify()}
            disabled={chainBusy}
            style={{
              appearance: 'none',
              padding: '0.5rem 0.9rem',
              borderRadius: '0.35rem',
              border: '1px solid var(--border-strong)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-2xs)',
              fontWeight: 500,
              cursor: chainBusy ? 'not-allowed' : 'pointer',
            }}
          >
            {chainBusy ? 'Verifying chain…' : 'Verify chain integrity'}
          </button>
        }
      />

      {chainReport ? <ChainVerifyBanner report={chainReport} /> : null}
      {chainError ? <Alert variant="error">{chainError}</Alert> : null}

      <FilterBar
        groups={EVENT_TYPE_GROUPS}
        selected={selectedTypes}
        onToggle={toggleType}
        onClear={clearTypes}
      />

      {events.loading ? (
        <Spinner label="Loading audit log" />
      ) : events.error ? (
        <Alert variant="error">{events.error}</Alert>
      ) : !events.data || events.data.events.length === 0 ? (
        <EmptyState
          title="No events in this window"
          description="Nothing has happened here yet — or your filters exclude everything."
        />
      ) : (
        <EventTable rows={events.data.events} count={events.data.count} />
      )}
    </div>
  );
}

// ---- Chain-verify banner ----------------------------------------------

function ChainVerifyBanner({ report }: { report: ChainVerifyReport }) {
  const clean = report.gaps.length === 0 && report.verified === report.total;
  const tone = clean ? 'success' : 'warning';
  const bg = clean ? 'var(--success-bg)' : 'var(--warning-bg)';
  const fg = clean ? 'var(--success-fg)' : 'var(--warning-fg)';
  const border = clean ? 'var(--band-green)' : 'var(--band-amber)';
  return (
    <section
      aria-label="Chain verify report"
      style={{
        border: `1px solid ${border}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: bg,
        padding: 'var(--space-3) var(--space-4)',
        color: fg,
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        fontSize: 'var(--font-size-2xs)',
      }}
    >
      <strong style={{ fontWeight: 700 }}>{clean ? '✓ Chain verified' : '⚠ Chain gaps'}</strong>
      <span>
        {report.verified} of {report.total} events verified
        {report.gaps.length > 0 ? ` · ${report.gaps.length} gap${report.gaps.length === 1 ? '' : 's'}` : ''}
      </span>
      {report.signing_key_fingerprint ? (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            color: fg,
            opacity: 0.8,
          }}
          title={report.signing_key_fingerprint}
        >
          key {report.signing_key_fingerprint.slice(0, 12)}…
        </span>
      ) : null}
      {report.window.since || report.window.until ? (
        <span style={{ fontSize: 'var(--font-size-3xs)', color: fg, opacity: 0.7 }}>
          window: {report.window.since ?? 'begin'} → {report.window.until ?? 'end'} · limit {report.window.limit}
        </span>
      ) : null}
      {tone === 'warning' && report.gaps.length > 0 ? (
        <ol
          style={{
            listStyle: 'decimal inside',
            margin: 'var(--space-2) 0 0',
            padding: 0,
            fontSize: 'var(--font-size-3xs)',
            color: fg,
            width: '100%',
          }}
        >
          {report.gaps.slice(0, 5).map((g) => (
            <li key={g.event_id}>
              <code style={{ fontFamily: 'var(--font-mono)' }}>{g.event_id}</code> — {g.reason}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

// ---- Filter bar --------------------------------------------------------

function FilterBar(props: {
  groups: Array<{ label: string; types: string[] }>;
  selected: Set<string>;
  onToggle: (t: string) => void;
  onClear: () => void;
}) {
  return (
    <section
      aria-label="Filters"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      {props.groups.map((g) => (
        <div key={g.label} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              minWidth: '5.5rem',
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-dim)',
              fontWeight: 600,
            }}
          >
            {g.label}
          </span>
          {g.types.map((t) => {
            const on = props.selected.has(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => props.onToggle(t)}
                aria-pressed={on}
                style={{
                  appearance: 'none',
                  padding: '0.2rem 0.55rem',
                  borderRadius: '999px',
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent)' : 'transparent',
                  color: on ? 'var(--text-inverse)' : 'var(--text-muted)',
                  fontSize: 'var(--font-size-3xs)',
                  cursor: 'pointer',
                  fontWeight: on ? 600 : 500,
                }}
              >
                {eventTypeLabel[t as AuditEventType] ?? t}
              </button>
            );
          })}
        </div>
      ))}
      {props.selected.size > 0 ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {props.selected.size} filter{props.selected.size === 1 ? '' : 's'} active
          </span>
          <button
            type="button"
            onClick={props.onClear}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              color: 'var(--accent)',
              fontSize: 'var(--font-size-3xs)',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Clear
          </button>
        </div>
      ) : null}
    </section>
  );
}

// ---- Event table -------------------------------------------------------

function EventTable(props: { rows: AuditEventRow[]; count: number }) {
  return (
    <section aria-label="Audit events" style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
        {props.count} event{props.count === 1 ? '' : 's'} in window
      </div>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          <thead>
            <tr
              style={{
                textAlign: 'left',
                background: 'var(--surface-alt)',
                borderBottom: '1px solid var(--border)',
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-muted)',
                fontWeight: 600,
              }}
            >
              <th style={{ padding: 'var(--space-2) var(--space-3)' }}>Event</th>
              <th style={{ padding: 'var(--space-2) var(--space-3)' }}>Target</th>
              <th style={{ padding: 'var(--space-2) var(--space-3)' }}>Actor</th>
              <th style={{ padding: 'var(--space-2) var(--space-3)' }}>Occurred</th>
              <th style={{ padding: 'var(--space-2) var(--space-3)', width: '2rem' }} aria-label="expand" />
            </tr>
          </thead>
          <tbody>
            {props.rows.map((r) => (
              <EventRow key={r.event_id} row={r} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EventRow({ row }: { row: AuditEventRow }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--border)' }}>
        <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text)', fontWeight: 500 }} title={row.event_type}>
              {eventTypeLabel[row.event_type as AuditEventType] ?? row.event_type}
            </span>
            <Badge>v{row.methodology_version}</Badge>
          </div>
        </td>
        <td style={{ padding: 'var(--space-2) var(--space-3)', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
          {row.target_kind}
          <span aria-hidden style={{ color: 'var(--text-dim)' }}> · </span>
          <span title={row.target_id} style={{ color: 'var(--text)' }}>{row.target_id}</span>
        </td>
        <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <ActorTypeChip type={row.actor_type} />
            <span title={row.actor_id} style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
              {row.actor_id}
            </span>
          </div>
        </td>
        <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--text-muted)' }}>
          <time dateTime={row.occurred_at}>{fmtDate(row.occurred_at)}</time>
        </td>
        <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'right' }}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={`ev-detail-${row.event_id}`}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              color: 'var(--text-dim)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-2xs)',
              padding: 0,
            }}
          >
            {open ? '▾' : '▸'}
          </button>
        </td>
      </tr>
      {open ? (
        <tr id={`ev-detail-${row.event_id}`}>
          <td colSpan={5} style={{ padding: 'var(--space-3) var(--space-4)', background: 'var(--surface-alt)' }}>
            <EventDetailInline row={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function EventDetailInline({ row }: { row: AuditEventRow }) {
  const before = safeParseJson(row.before_json);
  const after = safeParseJson(row.after_json);
  const ws = useOptionalWorkspace();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-3xs)' }}>
      {ws ? (
        <Link to={`/w/${ws.workspace.slug}/app/audit/events/${encodeURIComponent(row.event_id)}`}>Open event</Link>
      ) : null}
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '0.25rem 0.75rem',
          margin: 0,
          fontFamily: 'var(--font-mono)',
        }}
      >
        <dt style={{ color: 'var(--text-dim)' }}>event_id</dt>
        <dd style={{ margin: 0, color: 'var(--text)' }}>{row.event_id}</dd>
        <dt style={{ color: 'var(--text-dim)' }}>event_hash</dt>
        <dd style={{ margin: 0, color: 'var(--text)', wordBreak: 'break-all' }}>{row.event_hash}</dd>
        <dt style={{ color: 'var(--text-dim)' }}>chained_from</dt>
        <dd style={{ margin: 0, color: 'var(--text)' }}>{row.chained_from ?? '(chain root)'}</dd>
        <dt style={{ color: 'var(--text-dim)' }}>key_fingerprint</dt>
        <dd style={{ margin: 0, color: 'var(--text)' }}>{row.key_fingerprint}</dd>
        {row.reason_text ? (
          <>
            <dt style={{ color: 'var(--text-dim)' }}>reason</dt>
            <dd style={{ margin: 0, color: 'var(--text)', fontFamily: 'inherit' }}>{row.reason_text}</dd>
          </>
        ) : null}
      </dl>
      {before || after ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <JsonBlock label="Before" value={before} />
          <JsonBlock label="After" value={after} />
        </div>
      ) : null}
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <pre
        style={{
          margin: 0,
          padding: 'var(--space-2)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          fontSize: 'var(--font-size-3xs)',
          fontFamily: 'var(--font-mono)',
          overflow: 'auto',
          maxHeight: '12rem',
        }}
      >
        {value == null ? <span style={{ color: 'var(--text-dim)' }}>—</span> : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function ActorTypeChip({ type }: { type: 'user' | 'system' | 'assessor' }) {
  const tone: Record<typeof type, { bg: string; fg: string }> = {
    user: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)' },
    system: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)' },
    assessor: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' },
  };
  const t = tone[type];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: '3px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
      }}
    >
      {type}
    </span>
  );
}

function safeParseJson(input: string | null): unknown {
  if (input == null) return null;
  try {
    return JSON.parse(input);
  } catch {
    return input;
  }
}
